import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, catchError, of, BehaviorSubject, forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from "../../environments/environment";

export interface BenefitItem {
  id: number;
  titulo: string;
  img: string;
  url: string;
  orden: number;
}

export interface BenefitsResponse {
  tipoDispositivo: {
    xl: BenefitItem[];
    desktop: BenefitItem[];
    tablet: BenefitItem[];
    movil: BenefitItem[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class DiscountsService {
  private readonly baseUrl = environment.apiUrl+'/beneficios';
  private cachedResponses: { [key: string]: BenefitsResponse } = {};
  private lastFetchTimes: { [key: string]: number } = {};
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
  private isLoading: { [key: string]: boolean } = {};
  private currentRequests: { [key: string]: Observable<BenefitsResponse> } = {};

  constructor(private readonly http: HttpClient) {}

  // Método para obtener beneficios de una fila específica
  getBenefitsByRow(row: 'fila1' | 'fila2' | 'fila3'): Observable<BenefitsResponse> {
    const apiUrl = `${this.baseUrl}/${row}`;
    const now = Date.now();

    // Si ya hay una petición en curso para esta fila, retornar la misma
    if (this.isLoading[row] && this.currentRequests[row]) {
      return this.currentRequests[row]!;
    }

    // Retornar cache si está disponible y no ha expirado
    if (this.cachedResponses[row] && (now - (this.lastFetchTimes[row] || 0)) < this.CACHE_DURATION) {
      return of(this.cachedResponses[row]!);
    }

    // Si hay cache expirado, retornarlo mientras se hace nueva petición
    if (this.cachedResponses[row]) {
      return of(this.cachedResponses[row]!);
    }

    // Hacer nueva llamada
    console.log(`🔄 Beneficios ${row}: Iniciando nueva petición HTTP`);
    this.isLoading[row] = true;

    this.currentRequests[row] = this.http.get<BenefitsResponse>(apiUrl).pipe(
      tap(response => {
        console.log(`✅ Beneficios ${row}: Petición HTTP completada`);
        this.cachedResponses[row] = response;
        this.lastFetchTimes[row] = Date.now();
        this.isLoading[row] = false;
        delete this.currentRequests[row];
      }),
      catchError(error => {
        console.error(`❌ Beneficios ${row}: Error en petición HTTP:`, error);
        this.isLoading[row] = false;
        delete this.currentRequests[row];

        // Retornar cache anterior si existe, aunque haya expirado
        if (this.cachedResponses[row]) {
          return of(this.cachedResponses[row]!);
        }
        throw error;
      }),
      shareReplay(1) // Compartir el resultado con múltiples suscriptores
    );

    return this.currentRequests[row]!;
  }

  // Método para obtener todos los beneficios (combinando las 3 filas)
  getBenefits(): Observable<BenefitsResponse> {
    return forkJoin({
      fila1: this.getBenefitsByRow('fila1'),
      fila2: this.getBenefitsByRow('fila2'),
      fila3: this.getBenefitsByRow('fila3')
    }).pipe(
      map(responses => {
        // Combinar los beneficios de las 3 filas
        const combinedResponse: BenefitsResponse = {
          tipoDispositivo: {
            xl: [],
            desktop: [],
            tablet: [],
            movil: []
          }
        };

        const deviceTypes: ('xl' | 'desktop' | 'tablet' | 'movil')[] = ['xl', 'desktop', 'tablet', 'movil'];

        deviceTypes.forEach(deviceType => {
          // Combinar beneficios de todas las filas
          combinedResponse.tipoDispositivo[deviceType] = [
            ...(responses.fila1.tipoDispositivo[deviceType] || []),
            ...(responses.fila2.tipoDispositivo[deviceType] || []),
            ...(responses.fila3.tipoDispositivo[deviceType] || [])
          ];
        });

        return combinedResponse;
      })
    );
  }

  getBenefitsByDeviceType(deviceType: 'xl' | 'desktop' | 'tablet' | 'movil'): Observable<BenefitItem[]> {
    return this.getBenefits().pipe(
      map(response => response.tipoDispositivo[deviceType] || [])
    );
  }

  // Método para obtener beneficios de una fila específica para un tipo de dispositivo
  getBenefitsByRowAndDevice(row: 'fila1' | 'fila2' | 'fila3', deviceType: 'xl' | 'desktop' | 'tablet' | 'movil'): Observable<BenefitItem[]> {
    return this.getBenefitsByRow(row).pipe(
      map(response => response.tipoDispositivo[deviceType] || [])
    );
  }

  // Método para obtener todos los beneficios de una fila específica para todos los dispositivos
  getBenefitsByRowAllDevices(row: 'fila1' | 'fila2' | 'fila3'): Observable<{
    xl: BenefitItem[];
    desktop: BenefitItem[];
    tablet: BenefitItem[];
    movil: BenefitItem[];
  }> {
    return this.getBenefitsByRow(row).pipe(
      map(response => response.tipoDispositivo)
    );
  }

  getCurrentDeviceType(): 'xl' | 'desktop' | 'tablet' | 'movil' {
    const width = window.innerWidth;

    if (width >= 1200) return 'xl';
    if (width >= 769) return 'desktop'; // Cambiado de 992 a 769 para que coincida con el breakpoint
    // if (width >= 768) return 'tablet';
    return 'desktop'; // Corregido: dispositivos menores a 768px son móviles
  }

  // Método para actualizar el tipo de dispositivo (para compatibilidad con otros servicios)
  updateDeviceType(): void {
    // Este método existe para mantener compatibilidad con otros servicios
    // El tipo de dispositivo se detecta automáticamente en getCurrentDeviceType()
    console.log('📱 DiscountsService: Tipo de dispositivo actualizado a', this.getCurrentDeviceType());
  }

  // Método para limpiar cache (útil para testing o actualizaciones forzadas)
  clearCache(): void {
    console.log('🧹 Beneficios: Limpiando cache de todas las filas');
    this.cachedResponses = {};
    this.lastFetchTimes = {};
    this.isLoading = {};
    this.currentRequests = {};
  }

  // Método para limpiar cache de una fila específica
  clearCacheForRow(row: 'fila1' | 'fila2' | 'fila3'): void {
    console.log(`🧹 Beneficios: Limpiando cache de ${row}`);
    delete this.cachedResponses[row];
    delete this.lastFetchTimes[row];
    delete this.isLoading[row];
    delete this.currentRequests[row];
  }

  // Método para forzar actualización (ignora cache)
  forceRefresh(): Observable<BenefitsResponse> {
    console.log('🔄 Beneficios: Forzando actualización de todas las filas');
    this.clearCache();
    return this.getBenefits();
  }

  // Método para forzar actualización de una fila específica
  forceRefreshRow(row: 'fila1' | 'fila2' | 'fila3'): Observable<BenefitsResponse> {
    console.log(`🔄 Beneficios: Forzando actualización de ${row}`);
    this.clearCacheForRow(row);
    return this.getBenefitsByRow(row);
  }

  // Método para verificar estado del cache
  getCacheStatus(): {
    hasCache: boolean;
    isExpired: boolean;
    isLoading: boolean;
    rows: { [key: string]: { hasCache: boolean; isExpired: boolean; isLoading: boolean } }
  } {
    const now = Date.now();
    const rows = ['fila1', 'fila2', 'fila3'] as const;
    const rowStatus: { [key: string]: { hasCache: boolean; isExpired: boolean; isLoading: boolean } } = {};

    let hasAnyCache = false;
    let hasAnyExpired = false;
    let hasAnyLoading = false;

    rows.forEach(row => {
      const hasCache = !!this.cachedResponses[row];
      const isExpired = hasCache && (now - (this.lastFetchTimes[row] || 0)) >= this.CACHE_DURATION;
      const isLoading = !!this.isLoading[row];

      rowStatus[row] = { hasCache, isExpired, isLoading };

      if (hasCache) hasAnyCache = true;
      if (isExpired) hasAnyExpired = true;
      if (isLoading) hasAnyLoading = true;
    });

    return {
      hasCache: hasAnyCache,
      isExpired: hasAnyExpired,
      isLoading: hasAnyLoading,
      rows: rowStatus
    };
  }

  // Método para obtener estadísticas de beneficios por fila
  getBenefitsStatistics(): Observable<{
    fila1: { total: number; byDevice: { [key: string]: number } };
    fila2: { total: number; byDevice: { [key: string]: number } };
    fila3: { total: number; byDevice: { [key: string]: number } };
  }> {
    return forkJoin({
      fila1: this.getBenefitsByRow('fila1'),
      fila2: this.getBenefitsByRow('fila2'),
      fila3: this.getBenefitsByRow('fila3')
    }).pipe(
      map(responses => {
        const stats = {
          fila1: { total: 0, byDevice: {} as { [key: string]: number } },
          fila2: { total: 0, byDevice: {} as { [key: string]: number } },
          fila3: { total: 0, byDevice: {} as { [key: string]: number } }
        };

        const deviceTypes: ('xl' | 'desktop' | 'tablet' | 'movil')[] = ['xl', 'desktop', 'tablet', 'movil'];

        deviceTypes.forEach(deviceType => {
          const fila1Count = (responses.fila1.tipoDispositivo[deviceType] || []).length;
          const fila2Count = (responses.fila2.tipoDispositivo[deviceType] || []).length;
          const fila3Count = (responses.fila3.tipoDispositivo[deviceType] || []).length;

          stats.fila1.byDevice[deviceType] = fila1Count;
          stats.fila2.byDevice[deviceType] = fila2Count;
          stats.fila3.byDevice[deviceType] = fila3Count;

          stats.fila1.total += fila1Count;
          stats.fila2.total += fila2Count;
          stats.fila3.total += fila3Count;
        });

        return stats;
      })
    );
  }
}
